//This function will handle old deeplinks and redirect them to the new ones
import { Platform } from "react-native";
import { resolveVanitySlug } from "./vanityUrls";

const prefix = Platform.OS === "web" ? "/webapp" : "";
const appScheme =
  Platform.OS === "web"
    ? window.location.origin
    : process.env.EXPO_PUBLIC_APP_SCHEME;

const productPrefixMap: Record<string, string> = {
  diamond: "natural-diamonds",
  gemstone: "gemstones",
  jewelry: "jewelry",
  lab_grown_diamond: "lab-grown-diamonds",
};

const deeplinkRules = [
  {
    from: `${prefix}/favorites`,
    to: `${prefix}/collections`,
  },
  {
    from: `${prefix}/order/:price_mode/order_detail/:id`,
    to: `${prefix}/requests/:id`,
    extraParams: { requestName: "order" },
  },
  {
    from: `${prefix}/:productType/:price_mode/item_request/:id`,
    to: `${prefix}/requests/:id`,
  },
  {
    from: `${prefix}/:productType/:priceMode/item_detail/:productID`,
    to: `${prefix}/:productPrefix/item-detail/:productID`,
  },
  {
    from: `${prefix}/:productType/:priceMode/search`,
    to: `${prefix}/:productPrefix/search`,
  },
  {
    from: `${prefix}/search`,
    to: `${prefix}/:productPrefix/search/new`,
  },
];

function matchRoutePattern(pattern: string, path: string) {
  const paramNames: string[] = [];
  const regexStr = pattern.replace(/:([^/]+)/g, (_, key) => {
    paramNames.push(key);
    return "([^/]+)";
  });
  const regex = new RegExp(`^${regexStr}$`);
  const match = path.match(regex);
  if (!match) return null;

  const params: Record<string, string> = {};
  paramNames.forEach((key, i) => {
    params[key] = match[i + 1];
  });
  return params;
}

function replaceParams(template: string, params: Record<string, string>) {
  return template.replace(/:([^/]+)/g, (_, key) => params[key]);
}

export function rewriteDeeplink(url: string): string {
  try {
    if (Platform.OS !== "web" && url.match(/^https?:\/\/[^/]+\/webapp\//)) {
      url = url.replace(/^https?:\/\/[^/]+\/webapp\//, `${appScheme}://`);
    }
    const cleanedUrl = url.replace(/^[a-zA-Z]+:\/\/?/, "");
    const [pathnameWithQuery, queryStr] = cleanedUrl.split("?");
    let path = pathnameWithQuery.startsWith("/")
      ? pathnameWithQuery
      : `/${pathnameWithQuery}`;
    path = path.replace(/\/+$/, "");

    const vanityParams = matchRoutePattern(
      `${prefix}/search/result/:slug`,
      path
    );
    if (vanityParams?.slug) {
      const resolved = resolveVanitySlug(vanityParams.slug);
      if (resolved) {
        const search = new URLSearchParams();
        search.set("productType", resolved.productType);
        if (resolved.priceMode !== undefined) {
          search.set("priceMode", String(resolved.priceMode));
        }
        search.set("saved_search", resolved.savedSearch);
        if (resolved.sectionName) {
          search.set("sectionName", resolved.sectionName);
        }
        const userQuery = new URLSearchParams(queryStr || "");
        userQuery.forEach((value, key) => {
          search.set(key, value);
        });

        const newPath = `${prefix}/${resolved.productPrefix}/search`;
        const finalPathWithQuery = `${newPath}?${search.toString()}`;
        return `${appScheme}://${
          finalPathWithQuery.startsWith("/")
            ? finalPathWithQuery.slice(1)
            : finalPathWithQuery
        }`;
      }
    }

    for (const rule of deeplinkRules) {
      const params = matchRoutePattern(rule.from, path);
      if (params) {
        if (rule.to.includes(":productPrefix") && params.productType) {
          const mapped = productPrefixMap[params.productType];
          if (mapped) {
            params.productPrefix = mapped;
          }
        }

        const newPath = replaceParams(rule.to, params);

        const originalQuery = new URLSearchParams(queryStr || "");
        const mergedQuery = new URLSearchParams(originalQuery.toString());

        for (const [key, value] of Object.entries(params)) {
          if (!rule.to.includes(`:${key}`) && !mergedQuery.has(key)) {
            mergedQuery.set(key, value);
          }
        }

        if ("extraParams" in rule && rule.extraParams) {
          for (const [key, value] of Object.entries(rule.extraParams)) {
            if (!mergedQuery.has(key)) {
              mergedQuery.set(key, value);
            }
          }
        }

        const finalPathWithQuery = `${newPath}${
          mergedQuery.toString() ? "?" + mergedQuery.toString() : ""
        }`;

        const finalUrl = `${appScheme}://${
          finalPathWithQuery.startsWith("/")
            ? finalPathWithQuery.slice(1)
            : finalPathWithQuery
        }`;
        return finalUrl;
      }
    }
    return url;
  } catch (err) {
    console.error("[rewriteDeeplink] Error:", err);
    return url;
  }
}
